import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ItemResponse,
  ListResponse,
  NotificationResponse,
  NotificationType,
  UnreadCountResponse,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const KEY = ['notifications'] as const;
/** In-App-Benachrichtigungen werden gepollt (kein WebSocket) — alle 60 s. */
const POLL_MS = 60_000;
/** Seitengröße der eigenen Benachrichtigungs-Seite („Mehr laden"). */
export const NOTIFICATIONS_PAGE_SIZE = 20;

export interface NotificationFilter {
  unreadOnly?: boolean;
  type?: NotificationType | '';
}

function filterParams(filter: NotificationFilter): string {
  const params = new URLSearchParams();
  if (filter.unreadOnly) params.set('unreadOnly', 'true');
  if (filter.type) params.set('type', filter.type);
  return params.toString();
}

/** Eigene Benachrichtigungen (neueste zuerst) — für Glocke/Dashboard (feste Obergrenze). */
export function useNotifications(options: { limit?: number } = {}) {
  const limit = options.limit ?? 10;
  return useQuery({
    queryKey: [...KEY, 'list', limit],
    queryFn: () => apiRequest<ListResponse<NotificationResponse>>(`/v1/notifications?limit=${limit}`),
    refetchInterval: POLL_MS,
  });
}

/** Volle Historie mit Filter + „Mehr laden" (Offset-Paging) — für die Benachrichtigungs-Seite. */
export function useNotificationsInfinite(filter: NotificationFilter) {
  return useInfiniteQuery({
    queryKey: [...KEY, 'infinite', filter],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const params = filterParams(filter);
      const query = `limit=${NOTIFICATIONS_PAGE_SIZE}&offset=${pageParam}${params ? `&${params}` : ''}`;
      return apiRequest<ListResponse<NotificationResponse>>(`/v1/notifications?${query}`);
    },
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < lastPage.meta.total ? loaded : undefined;
    },
    refetchInterval: POLL_MS,
  });
}

/** Anzahl ungelesener Benachrichtigungen — speist die Glocke im Header. */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: [...KEY, 'unread-count'],
    queryFn: () => apiRequest<UnreadCountResponse>('/v1/notifications/unread-count'),
    refetchInterval: POLL_MS,
  });
}

/** Eine Benachrichtigung als gelesen markieren. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<NotificationResponse>>(`/v1/notifications/${id}/read`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Alle eigenen Benachrichtigungen als gelesen markieren. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ updated: number }>('/v1/notifications/read-all', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Eine Benachrichtigung wegwischen (soft-delete). */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/notifications/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
